import { getPickedEmployee } from '@/helpers/employee';
import { getSelectionPricing } from '@/helpers/getSelectionPricing';
import { servicesWithExtraTimeAddOns } from '@/helpers/service';
import { useAppSelector } from '@/hooks';
import { _s, _t } from '@/locale';
import { BookState } from '@/types/state/book';
import { resolvePriceListId } from '../serviceItem.helpers';
import { SelectionsBarProps, SelectionsBarState } from './SelectionsBar.types';

/**
 * Derives the presentational props for SelectionsBar from the
 * existing BookAppointment Redux state (state.book).
 *
 * The `state` (SelectionsBarState) is a required parameter — the caller
 * (page or container) is responsible for knowing which step in the flow
 * the user is on.
 *
 * Pricing is derived via the shared getSelectionPricing helper (same logic as
 * SummaryView): a localized "from" prefix (_s('from')) is prepended for variable
 * prices, and the price is left empty when the place/service settings hide prices.
 *
 * The price list is resolved with `resolvePriceListId` — the same resolver the
 * service rows price themselves through. The bar totals the rows the summary
 * renders, so anything else lets the sum disagree with its visible parts.
 */
export function useSelectionsBar(
  barState: SelectionsBarState,
  handlers: Pick<
    SelectionsBarProps,
    'onPickEmployee' | 'onPickAnyEmployee' | 'onSearchTimes' | 'onChangeEmployee' | 'onEdit'
  > = {},
): SelectionsBarProps {
  const { employee, place, services: pickedServices, campaigns } = useAppSelector((state) => state.book) as BookState;

  const pickedEmployee = employee != null ? getPickedEmployee(employee, place?.employees ?? []) : undefined;
  const priceListId = resolvePriceListId(pickedEmployee);

  // Build the service label: name of the first non-add-on service, fallback to first service.
  const mainService = (pickedServices ?? []).find((s) => !s?.about?.settings?.isAddOn) ?? pickedServices?.[0];
  // Past one row the bar reports a count instead of a name, counted off the same grouping
  // the summary renders (extra-time add-ons folded into their owner).
  const serviceRowCount = servicesWithExtraTimeAddOns(pickedServices ?? []).services.length;
  const serviceLabel = serviceRowCount > 1 ? _t('service_other', { count: serviceRowCount }) : mainService?.name ?? '';

  const { total, totalWithoutCampaigns, hasCampaignPrice, isPriceVariable, shouldShowPrices } = getSelectionPricing(
    pickedServices,
    place,
    priceListId,
    campaigns,
  );

  // Prepend the localized "from" prefix (_s('from')) for variable prices (mirrors SummaryView).
  const price = shouldShowPrices ? (isPriceVariable && total ? `${_s('from')} ${total}` : total) : '';

  // Employee display name for PickTime states
  const employeeName = pickedEmployee?.about?.name;
  const employeeImgUrl = pickedEmployee?.about?.avatar;

  return {
    state: barState,
    serviceLabel,
    price,
    originalPrice: shouldShowPrices && hasCampaignPrice ? totalWithoutCampaigns : undefined,
    employeeName,
    employeeImgUrl,
    ...handlers,
  };
}
